import React, { useState } from "react";
import { Stack, Box, Typography, Divider, Pagination } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Skeleton from "@mui/material/Skeleton";
import PartnerCard from "~/components/Partners/Card";
import PartnersClient from "../api/PartnersClient";
import { useApiCall } from "~/single-page/hooks/useApiCall";
import NoItemsMessage from "~/components/NoItemsMessage";
import SearchField from '~/components/SearchField';
import { useScrollToSection } from '~/single-page/hooks/useScrollToSection';
import { useNormalizedSearch } from '~/single-page/hooks/useNormalizedSearch';

const LoadingSkeleton = () => (
  <Box 
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: '1fr',
        md: 'repeat(2, 1fr)'
      },
      gap: 2
    }}
  >
    {Array.from(new Array(6)).map((_, index) => (
      <Skeleton key={index} variant="rectangular" height={118} />
    ))}
  </Box>
);

export default function PartnersPage() {
  const {
    data: partners = [],
    success,
    pending,
  } = useApiCall({
    apiCall: PartnersClient.partners,
    init: true,
  });

  useScrollToSection();

  const [searchTerm, setSearchTerm] = useState('');
  const [normalizedSearchTerm, setNormalizedSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const filteredPartners = useNormalizedSearch(partners || [], normalizedSearchTerm, [
    'name',
    'description'
  ]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(filteredPartners.length / itemsPerPage);
  const currentPartners = filteredPartners.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (pending) {
    return <LoadingSkeleton />;
  }

  if (!success || !partners?.length) {
    return (
      <Box sx={{ margin: '2rem 0' }}>
        <NoItemsMessage itemName="Instituição" gender="feminino" />
      </Box>
    );
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: '100%' }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, mt: 2, textAlign: 'center' }}>
          Rede Institucional
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 4, 
            textAlign: "center",
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          O projeto de extensão intitulado "CInBora Impactar" é uma iniciativa que visa promover
          a interação entre a Universidade Federal de Pernambuco (UFPE) e a comunidade por meio
          da colaboração com o Projeto Bora Impactar, da Secretaria de Ciência, Tecnologia e
          Inovação da Prefeitura do Recife. Destinado aos alunos da disciplina de Desenvolvimento
          de Software do curso de Sistemas de Informação, o projeto oferece uma oportunidade prática
          para que os estudantes desenvolvam soluções de software que atendam às necessidades de
          Organizações da Sociedade Civil (OSC/ONGs) e projetos sociais locais. Com foco na aplicação
          de habilidades técnicas em contextos reais, o "CInBora Impactar" busca não apenas aprimorar
          as competências dos discentes, mas também gerar um impacto social positivo, fortalecendo
          as atividades e a capacidade operativa das organizações parceiras do Recife. Ao promover essa 
          interação, o projeto reforça a indissociabilidade entre ensino, pesquisa e extensão, contribuindo
          para a formação de profissionais mais conscientes e comprometidos com a transformação social.
        </Typography>
      </Box>

      <SearchField 
        value={searchTerm}
        onChange={(e) => { 
          setSearchTerm(e.target.value);
          setNormalizedSearchTerm(e.target.normalizedValue);
          setPage(1);
        }}
        label="Pesquisar Parceiros..."
        placeholder="Digite o nome do parceiro ou descrição"
      />

      <Divider sx={{ width: "100%" }} />

      <Box>
        <Box 
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr',
              md: 'repeat(2, 1fr)'
            },
            gap: 2
          }}
        >
          {Array.isArray(currentPartners) && currentPartners.map((partner) => (
            <Box 
              key={partner.id}
              sx={{ 
                width: '100%',
                height: '100%'
              }}
            >
              <PartnerCard 
                partner={partner} 
                sx={{ height: '100%' }}
              />
            </Box>
          ))}
        </Box>

        {filteredPartners.length === 0 && searchTerm && (
          <Box sx={{ 
            width: '100%', 
            textAlign: 'center', 
            py: 4 
          }}>
            <Typography color="text.secondary">
              Nenhum parceiro encontrado com o termo "{searchTerm}"
            </Typography>
          </Box>
        )}

        {filteredPartners.length > itemsPerPage && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            mt: 4
          }}>
            <Pagination 
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}